<script setup lang="ts">
/**
 * THE document context menu — one component for the documents sidebar (`FileExplorer.vue`) and the
 * finder (`pages/app/docs/index.vue`) (MAR-957, decided by Alex 2026-09-23).
 *
 * The two surfaces had each grown their own menu and each carried half of the behaviour: the
 * sidebar had case/client move targets (#847), "Add to chat" and "Download" but hardcoded English
 * labels, no viewport clamp and no guard against moving a folder into itself; the finder had the
 * clamp, the guard and i18n but none of the rest. This is the union, and it is the only copy — a
 * behaviour added to one surface's menu is now a behaviour added to both.
 *
 * What stays with the surface is only what genuinely differs between them: HOW a rename is asked
 * (both use `RenameItemDialog` today, but the surface owns when it opens) and HOW a delete is
 * confirmed (the sidebar arms its header strip for a multi-delete, the finder its selection bar), so
 * those two are emitted. Everything the menu can finish on its own it finishes here.
 *
 * The TARGET of an action is the whole selection when the right-clicked row is part of a
 * multi-selection, otherwise just that row — `useDocumentContextMenu.open` keeps that invariant.
 */
import {
  Check, Download, FileText, Folder, FolderInput, MessageSquarePlus, Pencil, Trash2,
  Briefcase, Building2,
} from 'lucide-vue-next'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
} from '~/components/ui/dropdown-menu'
import type { ItemRow } from '~~/shared/items/itemRow'
import { isItemPending } from '~~/shared/items/itemPending'
import { useDocumentsStore } from '~/stores/documents'
import { useCasesStore } from '~/stores/cases'
import { useClientsStore } from '~/stores/clients'
import { useWebChatStore } from '~/stores/web-chat'
import { useLinkDocumentToGroup } from '~/composables/useLinkDocumentToGroup'
import { useOpenDocumentItem, type DocumentContextMenuState } from '~/composables/useDocumentContextMenu'
import { downloadItemFile } from '~/utils/downloadItemFile'
import { apiErrorMessage } from '~/utils/apiErrorMessage'

const props = defineProps<{ menu: DocumentContextMenuState | null }>()
const emit = defineEmits<{
  close: []
  rename: [item: ItemRow]
  delete: [ids: string[], item: ItemRow]
  error: [message: string]
}>()

const { t } = useI18n()
const openItem = useOpenDocumentItem()
const documents = useDocumentsStore()
// Read lazily, only while the menu is open: the web-chat store pulls the whole chat stack in with it,
// and a surface that never opens its menu has no reason to pay for that at mount.
const webChat = () => useWebChatStore()
const cases = useCasesStore()
const clients = useClientsStore()
const { linkDocumentToGroup } = useLinkDocumentToGroup()

watch(() => props.menu, (menu) => {
  if (menu) ensureDestinationsLoaded()
})

/**
 * The case/client destinations come from the cases and clients stores, not from whichever groups the
 * surface happens to be rendering: the finder browses FOLDERS, where no case group exists, and the
 * shell only loads cases at mount when the sidebar is grouped — so on a hard load of `/app/docs` the
 * stores can be empty. Loading them here is best-effort: a failure leaves the folder destinations,
 * which is the menu's behaviour before #847, and is logged rather than blocking the menu.
 */
function ensureDestinationsLoaded() {
  if (!cases.loaded && !cases.loading) {
    cases.listCases().catch(err => console.warn('[documents] case destinations unavailable', err))
  }
  if (!clients.loaded && !clients.loading) {
    clients.listClients().catch(err => console.warn('[documents] client destinations unavailable', err))
  }
}

// ── Position ──
// Anchored to the pointer through a zero-size virtual element; `ui/dropdown-menu`'s popper flips and
// shifts it to stay inside the viewport (MAR-948). That replaces the hand clamp against a guessed
// 200×220 box, which was wrong the moment the menu grew a row.
const anchor = computed(() => {
  const x = props.menu?.x ?? 0
  const y = props.menu?.y ?? 0
  return {
    getBoundingClientRect: () => ({ x, y, left: x, top: y, right: x, bottom: y, width: 0, height: 0 }) as DOMRect,
  }
})

// ── Targets ──
const targetIds = computed<string[]>(() => {
  const item = props.menu?.item
  if (!item) return []
  const selected = documents.selectedIds
  if (selected.has(item.id) && selected.size > 1) return [...selected]
  return [item.id]
})

const targetItems = computed<ItemRow[]>(() => {
  const item = props.menu?.item
  return targetIds.value
    .map(id => (id === item?.id ? item : documents.get(id)))
    .filter((r): r is ItemRow => !!r)
})

// ── Row facts ──
function isDocument(item: ItemRow) {
  return item.type === 'document'
}

// Attachable = a document whose text the chat can read; a row still processing, failed or not yet
// uploaded has nothing to attach.
function isAttachable(item: ItemRow) {
  return isDocument(item) && !isItemPending(item.status) && item.status !== 'error'
}

// Downloadable = a document whose bytes exist. An `uploading` row is an optimistic placeholder with
// nothing stored yet; `processing`/`error` rows do have their original file.
function isDownloadable(item: ItemRow) {
  return isDocument(item) && item.status !== 'uploading'
}

/**
 * "Move to" destinations.
 *
 * - The library root and every folder, EXCEPT a folder being moved and anything beneath it (moving
 *   a folder into its own subtree is a cycle the server would refuse), and except the folder every
 *   target already sits in (a no-op).
 * - Every existing case and client (#847), when everything being moved is a document: picking one
 *   LINKS the document there through the same reassignment the drag-and-drop drop target uses
 *   (`useLinkDocumentToGroup`) — never `/api/items/move`, which has no notion of a case or client.
 */
type MoveTarget =
  | { kind: 'folder'; key: string; label: string; path: string }
  | { kind: 'case' | 'project'; key: string; label: string; id: string }

const moveTargets = computed<MoveTarget[]>(() => {
  const moving = targetItems.value
  if (moving.length === 0) return []
  const movingFolders = moving.filter(i => i.type === 'folder').map(i => documents.itemFullPath(i))
  const insideMoved = (full: string) => movingFolders.some(own => full === own || full.startsWith(`${own}/`))
  const alreadyThere = (full: string) => moving.every(i => i.path === full)

  const targets: MoveTarget[] = [{ kind: 'folder', key: '/', label: t('documents.allDocuments'), path: '/' }]
  for (const f of documents.folders) {
    if (insideMoved(f.full_path) || alreadyThere(f.full_path)) continue
    targets.push({ kind: 'folder', key: f.full_path, label: f.full_path, path: f.full_path })
  }
  if (moving.every(isDocument)) {
    const ids = moving.map(i => i.id)
    const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name)
    // A case or client that already holds every target is where they are, not somewhere to go.
    for (const c of [...cases.byId.values()].sort(byName)) {
      if (ids.every(id => c.documentIds.includes(id))) continue
      targets.push({ kind: 'case', key: `case:${c.id}`, label: c.name, id: c.id })
    }
    for (const c of [...clients.byId.values()].sort(byName)) {
      const held = documents.clientDirectIds.get(c.id) ?? []
      if (ids.every(id => held.includes(id))) continue
      targets.push({ kind: 'project', key: `project:${c.id}`, label: c.name, id: c.id })
    }
  }
  return targets
})

// ── Actions ──
function close() {
  emit('close')
}

/**
 * Where focus goes when the menu closes. Escape, or an action that finishes here (Add to chat,
 * Download, Move), hands it back to the row or ⋮ button that opened the menu, as a trigger-opened
 * menu would. An action that opens something of its own (Open navigates; Rename and Delete open a
 * dialog) keeps it, and so does a press outside, which put focus where the user pressed.
 */
let focusHandedOff = false
function handOffFocus() {
  focusHandedOff = true
}
function onOpenChange(open: boolean) {
  if (!open) close()
}
function onCloseAutoFocus(e: Event) {
  e.preventDefault()
  const target = focusHandedOff ? null : shown.value?.returnFocus
  focusHandedOff = false
  target?.focus()
}
// The prop is null while the panel is still animating out, so the panel renders the last menu shown.
const shown = shallowRef<DocumentContextMenuState | null>(null)
watch(() => props.menu, (menu) => {
  if (menu) {
    shown.value = menu
    focusHandedOff = false
  }
}, { immediate: true })

function failed(err: any) {
  emit('error', apiErrorMessage(err, t('documents.contextMenu.actionFailed')))
}

function onOpen(item: ItemRow) {
  handOffFocus()
  close()
  openItem(item)
}

function onAddToChat(item: ItemRow) {
  webChat().attachDocument(item)
  close()
}

async function onDownload(item: ItemRow) {
  close()
  try {
    await downloadItemFile(`/api/items/${item.id}/file`, item.file_name || item.name)
  } catch (err) {
    failed(err)
  }
}

function onRename(item: ItemRow) {
  handOffFocus()
  close()
  emit('rename', item)
}

async function onMove(target: MoveTarget) {
  const ids = [...targetIds.value]
  close()
  try {
    if (target.kind === 'folder') {
      await documents.moveItems(ids, target.path)
      return
    }
    for (const id of ids) await linkDocumentToGroup(target.kind, target.id, id)
  } catch (err) {
    failed(err)
  }
}

function onDelete(item: ItemRow) {
  const ids = [...targetIds.value]
  handOffFocus()
  close()
  emit('delete', ids, item)
}
</script>

<template>
  <!-- Non-modal, so a right-click on another row while this is open reaches that row and reopens the
       menu there, instead of being swallowed by a modal layer. -->
  <DropdownMenu :open="!!menu" :modal="false" @update:open="onOpenChange">
    <DropdownMenuContent
      v-if="shown"
      data-testid="document-context-menu"
      class="min-w-[180px]"
      :reference="anchor"
      side="bottom"
      align="start"
      :side-offset="0"
      @interact-outside="handOffFocus"
      @close-auto-focus="onCloseAutoFocus"
    >
      <DropdownMenuItem class="text-xs" @select="onOpen(shown.item)">
        <FileText class="h-3.5 w-3.5 text-muted-foreground" />
        {{ t('common.open') }}
      </DropdownMenuItem>
      <DropdownMenuItem v-if="isAttachable(shown.item)" class="text-xs" @select="onAddToChat(shown.item)">
        <component :is="webChat().isAttached(shown.item.id) ? Check : MessageSquarePlus" class="h-3.5 w-3.5 text-muted-foreground" />
        {{ webChat().isAttached(shown.item.id) ? t('documents.contextMenu.inChatContext') : t('documents.contextMenu.addToChat') }}
      </DropdownMenuItem>
      <DropdownMenuItem v-if="isDownloadable(shown.item)" class="text-xs" @select="onDownload(shown.item)">
        <Download class="h-3.5 w-3.5 text-muted-foreground" />
        {{ t('common.download') }}
      </DropdownMenuItem>
      <DropdownMenuItem class="text-xs" @select="onRename(shown.item)">
        <Pencil class="h-3.5 w-3.5 text-muted-foreground" />
        {{ t('common.rename') }}
        <DropdownMenuShortcut>F2</DropdownMenuShortcut>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <!-- Move to — a real submenu: hover, click, Enter or ArrowRight opens it, ArrowLeft returns. -->
      <DropdownMenuSub>
        <DropdownMenuSubTrigger class="text-xs">
          <FolderInput class="h-3.5 w-3.5 text-muted-foreground" />
          {{ t('documents.moveTo') }}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent class="min-w-[180px] max-h-[220px]" :side-offset="4">
          <DropdownMenuItem
            v-for="target in moveTargets"
            :key="target.key"
            class="text-xs"
            @select="onMove(target)"
          >
            <Briefcase v-if="target.kind === 'case'" class="h-3 w-3 text-muted-foreground" />
            <Building2 v-else-if="target.kind === 'project'" class="h-3 w-3 text-muted-foreground" />
            <Folder v-else class="h-3 w-3 text-warning-ink" />
            <span class="truncate">{{ target.label }}</span>
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSeparator />

      <DropdownMenuItem variant="destructive" class="text-xs" @select="onDelete(shown.item)">
        <Trash2 class="h-3.5 w-3.5" />
        {{ t('common.delete') }}
        <DropdownMenuShortcut>⌫</DropdownMenuShortcut>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
